import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import classNames from 'classnames/bind';
import { Redirect } from 'react-router-dom';

import {
  DataBlocks,
  I18n,
  Menu,
  Link,
  Button,
  Preloader,
  ErrorBlock,
} from 'components';

import MessageDetails from './components/MessageDetails';

import { SmartTable } from '../../containers';
import { withViewAnalytics } from '../../utils/hocs/withViewAnalytics';

import {
  MESSAGES_TABLE_CONFIG,
  MESSAGES_ARE_DELETABLE,
} from './config';

import { PERIOD_MONTH } from './constants';

import {
  fetchInitialData,
  changeActiveFilter,
  deleteNotifications,
  updateMessageIfNeeded,
  fetchAllMessages,
} from './actions';

import {
  isDetailsRequired,
  isExpandable,
} from './common';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const getSelectedMessagesCount = (gridData) => {
  if (!MESSAGES_ARE_DELETABLE) return 0;
  if (!gridData) return 0;
  return gridData.reduce((acc, row) => (row.selected ? acc + 1 : acc), 0);
};

class Messages extends PureComponent {
  componentDidMount() {
    this.props.actions.fetchInitialData();
  }

  renderMessageDetails = (message) => {
    if (!isExpandable(message)
        || this.props.isDetailsFetching
         && isDetailsRequired(message)
    ) {
      return null;
    }

    return <MessageDetails {...message} />;
  };

  render() {
    const {
      actions,
      filters,
      items,
      activeFilter,
      selectedMessagesCount,
      period,
      error,
      isNotAllMessages,
      isFetching,
      isRestrictedAccess,
    } = this.props;

    if (isRestrictedAccess) {
      return <Redirect to="/" />;
    }

    if (!items && !isNotAllMessages) {
      return <Preloader size="80" />;
    }

    if (error || isNotAllMessages) {
      return (
        <ErrorBlock
          title={<I18n path="common.somethingWentWrong" />}
          refreshAction={() => actions.fetchInitialData(true)}
        />
      );
    }

    return (
      <DataBlocks.Container>
        <DataBlocks.Block
          title={<I18n path={`messages.title.${period}`} />}
        >
          {items.length ? (
            <>
              {filters && filters.length > 2 && (
                <Menu
                  className={cx('menu')}
                  labelProperty="title"
                  idProperty="code"
                  entries={filters}
                  activeEntry={filters.indexOf(activeFilter)}
                  onEntryClick={actions.changeActiveFilter}
                />
              )}
              <SmartTable
                pathToState="messages.table"
                config={MESSAGES_TABLE_CONFIG}
                expandRenderer={this.renderMessageDetails}
                onToggleExpandableRow={actions.updateMessageIfNeeded}
              />
              {MESSAGES_ARE_DELETABLE && (
                <div className={cx('actionContainer')}>
                  <Link
                    onClick={() => selectedMessagesCount && actions.deleteNotifications()}
                  >
                    <I18n path="messages.remove" />
                    {' '}
                    (
                    {selectedMessagesCount}
                    )
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className={cx('emptyMessage')}>
              <I18n path={`messages.emptyMessage.${period}`} />
            </div>
          )}

          {(period === PERIOD_MONTH || isFetching) && (
            <div className={cx('actionContainer')}>
              <Button
                onClick={actions.fetchAllMessages}
                isLoading={isFetching}
              >
                <I18n path="messages.loadMore" />
              </Button>
            </div>
          )}
        </DataBlocks.Block>
      </DataBlocks.Container>
    );
  }
}

function mapStateToProps({ messages }) {
  return {
    ...messages,
    selectedMessagesCount: getSelectedMessagesCount(messages.table.data),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      fetchInitialData,
      changeActiveFilter,
      deleteNotifications,
      updateMessageIfNeeded,
      fetchAllMessages,
    }, dispatch),
  };
}

Messages.propTypes = {
  actions: PropTypes.object,
  items: PropTypes.array,
  filters: PropTypes.array,
  activeFilter: PropTypes.object,
  selectedMessagesCount: PropTypes.number,
  period: PropTypes.string,
  error: PropTypes.string,
  isDetailsFetching: PropTypes.bool,
  isNotAllMessages: PropTypes.bool,
  isNotAllMessagesPopupOpened: PropTypes.bool,
  isFetching: PropTypes.bool,
  isRestrictedAccess: PropTypes.bool,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewAnalytics,
)(Messages);
